import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Textarea } from '../ui/textarea';
import ProductionCalendar from '../ui/ProductionCalendar';
import FoundationStock from '../ui/FoundationStock';
import { progressiveCompress } from '../../utils/imageCompression';
import { 
  TrendingUp, TrendingDown, DollarSign, Loader2, Plus, Trash2, 
  Beef, Milk, Egg, Wheat, Camera, Scale, Heart, Edit2, ChevronRight, X, Users, Baby, Star, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import { validateQuantity, validateDate, maxWeightForUnit } from '../../utils/validation';
import { todayLocal, daysAgoLocal, formatDateShort } from '../../utils/dates';

// Extract FastAPI error detail (string, array, or object) into a printable string.
const errMsg = (err, fallback = 'Something went wrong') => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || String(e)).join(', ');
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return err?.message || fallback;
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Animal icons
const animalIcons = {
  goat: '🐐',
  sheep: '🐑',
  cattle: '🐄'
};

// Proper plural forms for irregular nouns
const pluralize = (type, count) => {
  if (count === 1) return type.charAt(0).toUpperCase() + type.slice(1);
  const plurals = {
    goat: 'Goats',
    sheep: 'Sheep',  // Irregular - same singular and plural
    cattle: 'Cattle' // Irregular - same singular and plural
  };
  return plurals[type] || (type.charAt(0).toUpperCase() + type.slice(1) + 's');
};

const purposeLabels = {
  dairy: 'Dairy',
  meat: 'Meat',
  fiber: 'Fiber/Wool',
  dual: 'Dual Purpose'
};

const LivestockManager = () => {
  const { getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('goat');
  const [herds, setHerds] = useState({ goat: [], sheep: [], cattle: [] });
  const [breeds, setBreeds] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedHerd, setSelectedHerd] = useState(null);
  const [herdDetail, setHerdDetail] = useState(null);
  const [selectedHerdForAction, setSelectedHerdForAction] = useState(null);
  const [foundationAnimals, setFoundationAnimals] = useState([]); // For milk assignment
  const [allFoundationAnimals, setAllFoundationAnimals] = useState([]); // All animals across herds
  
  // Quick action dialogs
  const [showMilkDialog, setShowMilkDialog] = useState(false);
  const [showWeightDialog, setShowWeightDialog] = useState(false);
  const [showHealthDialog, setShowHealthDialog] = useState(false);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  
  // Quick action form state
  const [milkLog, setMilkLog] = useState({ quantity: '', unit: 'gallons', notes: '', date: todayLocal(), foundation_animal_id: '' });
  const [weightLog, setWeightLog] = useState({ weight: '', unit: 'lbs', notes: '' });
  const [healthRecord, setHealthRecord] = useState({ record_type: 'checkup', description: '', vet_name: '', cost: '', notes: '' });
  const [photoUrl, setPhotoUrl] = useState('');
  
  // Milk calendar data
  const [milkCalendarData, setMilkCalendarData] = useState({});
  
  // Birth stats
  const [birthStats, setBirthStats] = useState(null);
  const [showBirthStatsDialog, setShowBirthStatsDialog] = useState(false);
  
  // Cache helpers
  const getCachedData = (key) => {
    try {
      const cached = localStorage.getItem(`livestock_${key}`);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        // Cache valid for 30 minutes
        if (Date.now() - timestamp < 1800000) return data;
      }
    } catch (error) {
      console.warn('Cache read error:', error.message);
    }
    return null;
  };

  const setCachedData = (key, data) => {
    try {
      localStorage.setItem(`livestock_${key}`, JSON.stringify({ data, timestamp: Date.now() }));
    } catch (error) {
      console.warn('Cache write error:', error.message);
    }
  };
  
  // Form state
  const [newHerd, setNewHerd] = useState({
    name: '',
    breed: '',
    purpose: 'meat',
    head_count: 1,
    male_count: 0,
    female_count: 0,
    birth_date: '',
    purchase_date: '',
    source: '',
    notes: '',
    photo_url: ''
  });

  const fetchData = async () => {
    // Load cached breeds immediately (static data)
    const cachedBreeds = getCachedData('breeds');
    if (cachedBreeds) {
      setBreeds(cachedBreeds);
    }
    
    // Load cached herds for instant UI
    const cachedHerds = getCachedData('herds');
    if (cachedHerds) {
      setHerds(cachedHerds);
      setLoading(false);
    }

    try {
      // Fetch herds first (user data - priority)
      const [goatRes, sheepRes, cattleRes] = await Promise.all([
        axios.get(`${API_URL}/api/livestock/goat`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/livestock/sheep`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/livestock/cattle`, { headers: getAuthHeader() })
      ]);
      
      const herdsData = {
        goat: goatRes.data.herds || [],
        sheep: sheepRes.data.herds || [],
        cattle: cattleRes.data.herds || []
      };
      setHerds(herdsData);
      setCachedData('herds', herdsData);
      
      // Fetch breeds (static data - can be cached longer)
      if (!cachedBreeds) {
        const [goatBreeds, sheepBreeds, cattleBreeds] = await Promise.all([
          axios.get(`${API_URL}/api/livestock/breeds/goat`, { headers: getAuthHeader() }),
          axios.get(`${API_URL}/api/livestock/breeds/sheep`, { headers: getAuthHeader() }),
          axios.get(`${API_URL}/api/livestock/breeds/cattle`, { headers: getAuthHeader() })
        ]);
        
        const breedsData = {
          goat: goatBreeds.data.breeds || {},
          sheep: sheepBreeds.data.breeds || {},
          cattle: cattleBreeds.data.breeds || {}
        };
        setBreeds(breedsData);
        setCachedData('breeds', breedsData);
      }
    } catch (error) {
      console.error('Failed to fetch livestock:', error);
      if (!cachedHerds) {
        toast.error('Failed to load livestock data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMilkCalendar = async () => {
    try {
      const endDate = todayLocal();
      const startDate = daysAgoLocal(90);
      
      const res = await axios.get(
        `${API_URL}/api/livestock/milk-production/calendar/all?start_date=${startDate}&end_date=${endDate}`,
        { headers: getAuthHeader() }
      );
      setMilkCalendarData(res.data);
    } catch (error) {
      console.error('Failed to fetch milk calendar:', error);
    }
  };

  const fetchBirthStats = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/birth-events/stats/season`,
        { headers: getAuthHeader() }
      );
      setBirthStats(res.data);
    } catch (error) {
      console.error('Failed to fetch birth stats:', error);
    }
  };

  const fetchAllFoundationAnimals = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/animals/foundation`,
        { headers: getAuthHeader() }
      );
      // Filter to only livestock (goat, sheep, cattle)
      const livestock = (res.data.animals || []).filter(a => 
        ['goat', 'sheep', 'cattle'].includes(a.animal_type)
      );
      setAllFoundationAnimals(livestock);
    } catch (error) {
      console.error('Failed to fetch foundation animals:', error);
    }
  };

  useEffect(() => {
    // Show cached data immediately
    const cachedHerds = getCachedData('herds');
    const cachedBreeds = getCachedData('breeds');
    if (cachedHerds) setHerds(cachedHerds);
    if (cachedBreeds) setBreeds(cachedBreeds);
    if (cachedHerds && cachedBreeds) setLoading(false);
    
    fetchData();
    fetchMilkCalendar();
    fetchBirthStats();
    fetchAllFoundationAnimals();
  }, []);

  // Check for deep link to animal profile from dashboard
  useEffect(() => {
    const storedProfile = localStorage.getItem('openAnimalProfile');
    if (!storedProfile) return;
    
    // Only process if we have herds loaded
    const hasHerds = herds.goat?.length > 0 || herds.sheep?.length > 0 || herds.cattle?.length > 0;
    
    if (!hasHerds) return;
    
    try {
      const profileData = JSON.parse(storedProfile);
      const { animalId, herdId, source, timestamp, isLivestock, isPoultry } = profileData;
      
      // Only use if less than 30 seconds old (give more time for page load)
      if (Date.now() - timestamp > 30000) {
        localStorage.removeItem('openAnimalProfile');
        return;
      }
      
      // Skip if this is for poultry
      if (isPoultry) {
        return;
      }
      
      if (!isLivestock && source !== 'foundation') {
        localStorage.removeItem('openAnimalProfile');
        return;
      }
      
      if (!herdId) {
        localStorage.removeItem('openAnimalProfile');
        toast.info('Animal found! Look in your herds below.');
        return;
      }
      
      // Find the herd and its animal type
      for (const animalType of ['goat', 'sheep', 'cattle']) {
        const herdList = herds[animalType] || [];
        const herd = herdList.find(h => h.id === herdId);
        if (herd) {
          // Clear storage first to prevent re-triggering
          localStorage.removeItem('openAnimalProfile');
          
          // Set the active tab
          setActiveTab(animalType);
          
          // Open the herd dialog first, then set the animal to open
          viewHerdDetail(animalType, herdId).then(() => {
            setTimeout(() => {
              localStorage.setItem('openFoundationAnimal', animalId);
            }, 500);
          });
          return;
        }
      }
      
      // Herd not found
      localStorage.removeItem('openAnimalProfile');
      toast.info('Herd not found. The animal may have been moved.');
    } catch (e) {
      localStorage.removeItem('openAnimalProfile');
    }
  }, [herds]);

  const handleCreateHerd = async () => {
    const breedName = newHerd.breed === '__custom__' ? newHerd.customBreed : newHerd.breed;
    if (!newHerd.name || !breedName) {
      toast.error('Please enter name and breed');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/livestock/${activeTab}`, {
        ...newHerd,
        breed: breedName,
        animal_type: activeTab
      }, { headers: getAuthHeader() });
      
      toast.success(`${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} herd added!`);
      setShowAddDialog(false);
      setNewHerd({
        name: '', breed: '', purpose: 'meat', head_count: 1,
        male_count: 0, female_count: 0,
        birth_date: '', purchase_date: '', source: '', notes: '', photo_url: '',
        customBreed: ''
      });
      // Clear cache to force fresh data
      localStorage.removeItem('livestock_herds');
      fetchData();
    } catch (error) {
      toast.error('Failed to create herd');
    }
  };

  const handleDeleteHerd = async (animalType, herdId) => {
    if (!window.confirm('Delete this herd? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API_URL}/api/livestock/${animalType}/${herdId}`, { 
        headers: getAuthHeader() 
      });
      toast.success('Herd deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete herd');
    }
  };

  // Edit-herd dialog state — only safe fields (name/breed/head_count/notes/
  // male_count/female_count/photo_url). animal_type is immutable to avoid
  // cascading updates to child records.
  const [editingHerd, setEditingHerd] = useState(null);
  const [editHerdSaving, setEditHerdSaving] = useState(false);

  const openEditHerd = (herd) => {
    setEditingHerd({
      id: herd.id,
      animal_type: herd.animal_type,
      name: herd.name || '',
      breed: herd.breed || '',
      head_count: herd.head_count ?? 1,
      male_count: herd.male_count ?? 0,
      female_count: herd.female_count ?? 0,
      notes: herd.notes || '',
      photo_url: herd.photo_url || '',
    });
  };

  const saveEditHerd = async () => {
    if (!editingHerd) return;
    if (!editingHerd.name.trim()) { toast.error('Name is required'); return; }
    const hc = Number(editingHerd.head_count);
    if (!Number.isFinite(hc) || hc < 1) { toast.error('Head count must be at least 1'); return; }
    setEditHerdSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/livestock/${editingHerd.animal_type}/${editingHerd.id}`,
        {
          name: editingHerd.name,
          breed: editingHerd.breed,
          head_count: parseInt(editingHerd.head_count, 10),
          male_count: parseInt(editingHerd.male_count, 10) || 0,
          female_count: parseInt(editingHerd.female_count, 10) || 0,
          notes: editingHerd.notes,
          photo_url: editingHerd.photo_url || null,
        },
        { headers: getAuthHeader() }
      );
      toast.success('Herd updated');
      setEditingHerd(null);
      fetchData();
    } catch (error) {
      toast.error(errMsg(error, 'Failed to update herd'));
    } finally {
      setEditHerdSaving(false);
    }
  };

  const viewHerdDetail = async (animalType, herdId) => {
    try {
      const res = await axios.get(`${API_URL}/api/livestock/${animalType}/${herdId}`, {
        headers: getAuthHeader()
      });
      setHerdDetail(res.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Failed to load herd details');
    }
  };

  // Quick action handlers
  const fetchFoundationAnimalsForHerd = async (herdId, animalType) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/animals/foundation?animal_type=${animalType}&group_id=${herdId}`,
        { headers: getAuthHeader() }
      );
      // Filter for FEMALES ONLY - only females can produce milk
      const femaleAnimals = (res.data.animals || []).filter(a => {
        const sex = a.sex || a.gender || 'unknown';
        return sex === 'female';
      });
      setFoundationAnimals(femaleAnimals);
    } catch (error) {
      console.error('Failed to fetch foundation animals:', error);
      setFoundationAnimals([]);
    }
  };

  const handleLogMilk = async () => {
    if (!selectedHerdForAction) { toast.error('Please select a herd'); return; }
    const q = validateQuantity(milkLog.quantity, { field: 'Quantity', max: 50 });
    if (!q.ok) { toast.error(q.message); return; }
    const dv = validateDate(milkLog.date, { field: 'Date' });
    // For the LivestockManager herd-level dialog we don't yet expose a
    // historical-confirm modal — just block invalid dates.
    if (!dv.ok && dv.reason !== 'too_old') { toast.error(dv.message); return; }

    try {
      await axios.post(`${API_URL}/api/livestock/milk-production`, {
        animal_id: selectedHerdForAction.id,
        animal_type: activeTab,
        herd_id: selectedHerdForAction.id,
        quantity: parseFloat(milkLog.quantity),
        unit: milkLog.unit,
        date: milkLog.date || todayLocal(),
        notes: milkLog.notes,
        foundation_animal_id: milkLog.foundation_animal_id || null,
        allow_historical: !dv.ok && dv.reason === 'too_old'
      }, { headers: getAuthHeader() });
      
      toast.success('Milk production logged!');
      setShowMilkDialog(false);
      setMilkLog({ quantity: '', unit: 'gallons', notes: '', date: todayLocal(), foundation_animal_id: '' });
      setFoundationAnimals([]);
      fetchMilkCalendar();
    } catch (error) {
      toast.error(errMsg(error, 'Failed to log milk'));
    }
  };

  const handleLogWeight = async () => {
    if (!selectedHerdForAction) { toast.error('Please select a herd'); return; }
    const q = validateQuantity(weightLog.weight, { field: 'Weight', max: maxWeightForUnit(weightLog.unit) });
    if (!q.ok) { toast.error(q.message); return; }

    try {
      await axios.post(`${API_URL}/api/livestock/weight`, {
        animal_id: selectedHerdForAction.id,
        animal_type: activeTab,
        weight: parseFloat(weightLog.weight),
        unit: weightLog.unit,
        notes: weightLog.notes
      }, { headers: getAuthHeader() });
      
      toast.success('Weight logged!');
      setShowWeightDialog(false);
      setWeightLog({ weight: '', unit: 'lbs', notes: '' });
    } catch (error) {
      toast.error(errMsg(error, 'Failed to log weight'));
    }
  };

  const handleAddHealth = async () => {
    if (!selectedHerdForAction || !healthRecord.description) {
      toast.error('Please select a herd and enter description');
      return;
    }
    if (healthRecord.cost && Number(healthRecord.cost) < 0) {
      toast.error('Cost cannot be negative');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/livestock/health`, {
        animal_id: selectedHerdForAction.id,
        animal_type: activeTab,
        record_type: healthRecord.record_type,
        description: healthRecord.description,
        vet_name: healthRecord.vet_name,
        cost: healthRecord.cost ? parseFloat(healthRecord.cost) : null,
        notes: healthRecord.notes
      }, { headers: getAuthHeader() });
      
      toast.success('Health record added!');
      setShowHealthDialog(false);
      setHealthRecord({ record_type: 'checkup', description: '', vet_name: '', cost: '', notes: '' });
    } catch (error) {
      toast.error(errMsg(error, 'Failed to add health record'));
    }
  };

  const handleUpdatePhoto = async () => {
    if (!selectedHerdForAction || !photoUrl) {
      toast.error('Please select a herd and enter photo URL');
      return;
    }
    
    try {
      await axios.put(`${API_URL}/api/livestock/${activeTab}/${selectedHerdForAction.id}`, {
        photo_url: photoUrl
      }, { headers: getAuthHeader() });
      
      toast.success('Photo updated!');
      setShowPhotoDialog(false);
      setPhotoUrl('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update photo');
    }
  };

  const openQuickAction = (action, herd) => {
    setSelectedHerdForAction(herd);
    switch (action) {
      case 'milk': 
        setShowMilkDialog(true);
        fetchFoundationAnimalsForHerd(herd.id, activeTab);
        break;
      case 'weight': setShowWeightDialog(true); break;
      case 'health': setShowHealthDialog(true); break;
      case 'photo': setShowPhotoDialog(true); break;
    }
  };

  const getBreedsList = (animalType) => {
    const animalBreeds = breeds[animalType] || {};
    const allBreeds = [];
    Object.entries(animalBreeds).forEach(([category, breedList]) => {
      breedList.forEach(b => allBreeds.push({ ...b, category }));
    });
    return allBreeds;
  };

  const getTotalCount = (animalType) => {
    return herds[animalType]?.reduce((sum, h) => sum + (h.head_count || 0), 0) || 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="livestock-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in w-full overflow-hidden" data-testid="livestock-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
            Livestock Manager
          </h1>
          <p className="text-muted-foreground mt-1">
            Track your goats, sheep, and cattle
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => { window.location.href = '/app/reports?template=grant_application&include=inventory,breeding,health'; }}
            data-testid="export-to-report-livestock-btn"
            className="text-xs"
          >
            Export to Report
          </Button>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button data-testid="add-livestock-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {animalIcons[activeTab]} Add New {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Herd
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium">Herd/Animal Name *</label>
                <Input
                  placeholder="e.g., Dairy Does, Bull Pen"
                  value={newHerd.name}
                  onChange={(e) => setNewHerd({ ...newHerd, name: e.target.value })}
                  data-testid="herd-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Breed *</label>
                <Select 
                  value={newHerd.breed === '__custom__' ? '__custom__' : newHerd.breed} 
                  onValueChange={(v) => {
                    if (v === '__custom__') {
                      setNewHerd({ ...newHerd, breed: '__custom__', customBreed: '' });
                    } else {
                      setNewHerd({ ...newHerd, breed: v, customBreed: '' });
                    }
                  }}
                >
                  <SelectTrigger data-testid="breed-select">
                    <SelectValue placeholder="Select breed" />
                  </SelectTrigger>
                  <SelectContent>
                    {getBreedsList(activeTab).map((b) => (
                      <SelectItem key={b.name} value={b.name}>
                        {b.name} ({b.category})
                      </SelectItem>
                    ))}
                    <SelectItem value="__custom__" className="border-t mt-1 pt-1">
                      ✨ Custom / Hybrid Breed
                    </SelectItem>
                  </SelectContent>
                </Select>
                {newHerd.breed === '__custom__' && (
                  <Input
                    className="mt-2"
                    placeholder="Enter custom breed name"
                    value={newHerd.customBreed || ''}
                    onChange={(e) => setNewHerd({ ...newHerd, customBreed: e.target.value })}
                    data-testid="custom-breed-input"
                  />
                )}
              </div>
              <div>
                <label className="text-sm font-medium">Purpose</label>
                <Select 
                  value={newHerd.purpose} 
                  onValueChange={(v) => setNewHerd({ ...newHerd, purpose: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dairy">Dairy</SelectItem>
                    <SelectItem value="meat">Meat</SelectItem>
                    <SelectItem value="fiber">Fiber/Wool</SelectItem>
                    <SelectItem value="dual">Dual Purpose</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-sm font-medium">Total Head</label>
                  <Input
                    type="number"
                    min="1"
                    value={newHerd.head_count === '' ? '' : newHerd.head_count}
                    onChange={(e) => setNewHerd({ ...newHerd, head_count: e.target.value === '' ? '' : parseInt(e.target.value) || '' })}
                    onBlur={(e) => {
                      if (e.target.value === '' || parseInt(e.target.value) < 1) {
                        setNewHerd({ ...newHerd, head_count: 1 });
                      }
                    }}
                    data-testid="head-count-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium flex items-center gap-1">
                    <span className="text-pink-500">♀</span> Females
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={newHerd.female_count === '' ? '' : newHerd.female_count}
                    onChange={(e) => setNewHerd({ ...newHerd, female_count: e.target.value === '' ? '' : parseInt(e.target.value) || '' })}
                    onBlur={(e) => {
                      if (e.target.value === '') {
                        setNewHerd({ ...newHerd, female_count: 0 });
                      }
                    }}
                    data-testid="female-count-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium flex items-center gap-1">
                    <span className="text-blue-500">♂</span> Males
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={newHerd.male_count === '' ? '' : newHerd.male_count}
                    onChange={(e) => setNewHerd({ ...newHerd, male_count: e.target.value === '' ? '' : parseInt(e.target.value) || '' })}
                    onBlur={(e) => {
                      if (e.target.value === '') {
                        setNewHerd({ ...newHerd, male_count: 0 });
                      }
                    }}
                    data-testid="male-count-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Birth Date</label>
                  <Input
                    type="date"
                    value={newHerd.birth_date}
                    onChange={(e) => setNewHerd({ ...newHerd, birth_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Purchase Date</label>
                  <Input
                    type="date"
                    value={newHerd.purchase_date}
                    onChange={(e) => setNewHerd({ ...newHerd, purchase_date: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Source</label>
                <Input
                  placeholder="Where acquired from"
                  value={newHerd.source}
                  onChange={(e) => setNewHerd({ ...newHerd, source: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Photo (Optional)
                </label>
                {newHerd.photo_url ? (
                  <div className="relative rounded-lg overflow-hidden border mt-2">
                    <img 
                      src={newHerd.photo_url} 
                      alt="Herd preview" 
                      className="w-full h-24 object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    <button
                      onClick={() => setNewHerd({ ...newHerd, photo_url: '' })}
                      className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full text-white hover:bg-black/80 transition-colors"
                      type="button"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label 
                    htmlFor="add-herd-photo" 
                    className="flex flex-col items-center justify-center w-full h-20 mt-2 border-2 border-dashed border-muted-foreground/30 rounded-lg cursor-pointer hover:border-primary/50 hover:bg-muted/20 transition-colors active:bg-muted/30"
                    onClick={() => document.getElementById('add-herd-photo')?.click()}
                  >
                    <input
                      id="add-herd-photo"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (!file.type.startsWith('image/')) {
                          toast.error('Please select an image');
                          return;
                        }
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('category', 'livestock');
                        try {
                          const res = await axios.post(`${API_URL}/api/upload/photo`, formData, {
                            headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                          });
                          setNewHerd({ ...newHerd, photo_url: res.data.url.startsWith('http') ? res.data.url : `${API_URL}${res.data.url}` });
                          toast.success('Photo uploaded!');
                        } catch (err) {
                          toast.error('Upload failed');
                        }
                      }}
                      className="sr-only"
                    />
                    <Upload className="w-5 h-5 text-muted-foreground mb-1" />
                    <span className="text-xs text-muted-foreground">Tap to add photo</span>
                  </label>
                )}
              </div>
              <div>
                <label className="text-sm font-medium">Notes</label>
                <Input
                  placeholder="Any additional notes"
                  value={newHerd.notes}
                  onChange={(e) => setNewHerd({ ...newHerd, notes: e.target.value })}
                />
              </div>
              <Button onClick={handleCreateHerd} className="w-full" data-testid="save-herd-btn">
                <Plus className="w-4 h-4 mr-2" />
                {newHerd.head_count <= 1 ? 'Add Animal' : 'Add Herd'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-1.5 sm:gap-4 max-w-full p-0.5">
        {['goat', 'sheep', 'cattle'].map((animal) => (
          <Card 
            key={animal}
            className={`card-farm cursor-pointer transition-all ${activeTab === animal ? 'ring-2 ring-primary bg-primary/5' : ''}`}
            onClick={() => setActiveTab(animal)}
            data-testid={`${animal}-summary-card`}
          >
            <CardContent className="py-2 sm:py-4 text-center px-1 sm:px-4">
              <span className="text-xl sm:text-3xl">{animalIcons[animal]}</span>
              <p className="font-heading text-lg sm:text-2xl font-bold mt-0.5">{getTotalCount(animal)}</p>
              <p className="text-[10px] sm:text-sm text-muted-foreground">{pluralize(animal, getTotalCount(animal))}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Milk Production Calendar */}
      {(milkCalendarData.total > 0 || herds.goat?.some(h => h.purpose === 'dairy') || herds.cattle?.some(h => h.purpose === 'dairy')) && (
        <ProductionCalendar
          data={milkCalendarData}
          title="Milk Production Calendar"
          unit="gallons"
          icon={Milk}
          colorTheme="blue"
          onDateClick={(date, total) => {
            toast.info(`${date}: ${total} gallons collected`);
          }}
        />
      )}

      {/* Birth Season Summary */}
      {birthStats && birthStats.total_babies > 0 && (
        <Card className="card-farm border-pink-200 bg-pink-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading flex items-center gap-2 text-base">
              <Baby className="w-5 h-5 text-pink-500" />
              Kidding/Lambing Season
              <span className="text-xs font-normal text-muted-foreground ml-auto">
                {birthStats.period?.start && new Date(birthStats.period.start).toLocaleDateString()} - {birthStats.period?.end && new Date(birthStats.period.end).toLocaleDateString()}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-4 gap-3 text-center">
              <div className="p-2 rounded-lg bg-white/70">
                <p className="text-xl font-bold text-pink-700">{birthStats.total_births}</p>
                <p className="text-xs text-pink-600">Births</p>
              </div>
              <div className="p-2 rounded-lg bg-white/70">
                <p className="text-xl font-bold text-blue-700">{birthStats.total_babies}</p>
                <p className="text-xs text-blue-600">Babies</p>
              </div>
              <div className="p-2 rounded-lg bg-white/70">
                <p className="text-xl font-bold text-green-700">{birthStats.outcomes?.thriving_with_mom || 0}</p>
                <p className="text-xs text-green-600">Thriving</p>
              </div>
              <div className="p-2 rounded-lg bg-white/70">
                <p className="text-xl font-bold text-amber-700">{birthStats.livability_rate || 0}%</p>
                <p className="text-xs text-amber-600">Success</p>
              </div>
            </div>
            
            {/* Outcome breakdown */}
            <div className="mt-3 flex flex-wrap gap-2">
              {birthStats.outcomes?.bottle_baby > 0 && (
                <span className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-700">
                  {birthStats.outcomes.bottle_baby} bottle {birthStats.outcomes.bottle_baby === 1 ? 'baby' : 'babies'}
                </span>
              )}
              {birthStats.outcomes?.fostered > 0 && (
                <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                  {birthStats.outcomes.fostered} fostered
                </span>
              )}
              {birthStats.outcomes?.stillborn > 0 && (
                <span className="text-xs px-2 py-1 rounded bg-gray-200 text-gray-600">
                  {birthStats.outcomes.stillborn} stillborn
                </span>
              )}
              {birthStats.outcomes?.did_not_make_it > 0 && (
                <span className="text-xs px-2 py-1 rounded bg-gray-200 text-gray-600">
                  {birthStats.outcomes.did_not_make_it} lost after birth
                </span>
              )}
            </div>
            
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full mt-2 text-pink-600 hover:text-pink-700 hover:bg-pink-100"
              onClick={() => setShowBirthStatsDialog(true)}
              data-testid="view-birth-stats-btn"
            >
              View Full Season Report
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Your Animals - Quick Access to Foundation Stock */}
      {allFoundationAnimals.filter(a => a.animal_type === activeTab).length > 0 && (
        <Card className="card-farm border-amber-200 bg-amber-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading flex items-center gap-2 text-base">
              <Star className="w-5 h-5 text-amber-500" />
              Your {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}s
              <span className="ml-auto text-xs font-normal text-muted-foreground">
                {allFoundationAnimals.filter(a => a.animal_type === activeTab).length} animals
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {allFoundationAnimals
                .filter(a => a.animal_type === activeTab)
                .slice(0, 8)
                .map((animal) => {
                  const herd = herds[activeTab]?.find(h => h.id === animal.group_id);
                  return (
                    <div 
                      key={animal.id}
                      className="p-2 rounded-lg border bg-white/70 hover:bg-white cursor-pointer transition-colors"
                      onClick={() => {
                        if (herd) {
                          viewHerdDetail(activeTab, herd.id);
                        }
                      }}
                      data-testid={`quick-animal-${animal.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`text-sm ${animal.sex === 'female' ? 'text-pink-500' : 'text-blue-500'}`}>
                          {animal.sex === 'female' ? '♀' : '♂'}
                        </span>
                        <span className="font-medium text-sm truncate">{animal.name}</span>
                      </div>
                      {animal.tag_number && (
                        <p className="text-xs text-muted-foreground">#{animal.tag_number}</p>
                      )}
                      <p className="text-xs text-muted-foreground truncate">
                        {animal.breed} {herd ? `• ${herd.name}` : ''}
                      </p>
                    </div>
                  );
                })}
            </div>
            {allFoundationAnimals.filter(a => a.animal_type === activeTab).length > 8 && (
              <p className="text-xs text-center text-amber-600 mt-2">
                +{allFoundationAnimals.filter(a => a.animal_type === activeTab).length - 8} more animals
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Herds List */}
      <Card className="card-farm">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            {animalIcons[activeTab]} {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Herds
          </CardTitle>
        </CardHeader>
        <CardContent>
          {herds[activeTab]?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No {activeTab}s yet. Add your first herd to get started!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {herds[activeTab]?.map((herd) => {
                const trackedCount = allFoundationAnimals.filter(
                  (a) => a.animal_type === activeTab && a.group_id === herd.id
                ).length;
                return (
                <div 
                  key={herd.id}
                  className="flex items-center gap-4 p-4 rounded-lg border hover:bg-muted/30 transition-colors"
                  data-testid={`herd-${herd.id}`}
                >
                  {herd.photo_url ? (
                    <img 
                      src={herd.photo_url} 
                      alt={herd.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center text-2xl">
                      {animalIcons[activeTab]}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium">{herd.name}</h3>
                    <p className="text-sm text-muted-foreground truncate">
                      {herd.breed} • {herd.head_count} head
                      {(herd.female_count > 0 || herd.male_count > 0) && (
                        <span className="ml-1">
                          (<span className="text-pink-500">♀{herd.female_count || 0}</span>/<span className="text-blue-500">♂{herd.male_count || 0}</span>)
                        </span>
                      )}
                      {' • '}{purposeLabels[herd.purpose] || herd.purpose}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5" data-testid={`herd-count-detail-${herd.id}`}>
                      {herd.head_count} in herd · {trackedCount} tracked individually
                    </p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      herd.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'
                    }`}>
                      {herd.status}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openEditHerd(herd)}
                      data-testid={`edit-herd-${herd.id}`}
                      aria-label="Edit herd"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => viewHerdDetail(activeTab, herd.id)}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDeleteHerd(activeTab, herd.id)}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Herd Dialog — safe fields only. Species is immutable to avoid
          cascading updates on child records. */}
      <Dialog open={!!editingHerd} onOpenChange={(v) => !v && setEditingHerd(null)}>
        <DialogContent className="max-w-md" data-testid="edit-herd-dialog">
          <DialogHeader>
            <DialogTitle>Edit herd</DialogTitle>
          </DialogHeader>
          {editingHerd && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="edit-herd-name">Name</Label>
                <Input id="edit-herd-name" data-testid="edit-herd-name-input"
                  value={editingHerd.name}
                  onChange={(e) => setEditingHerd({ ...editingHerd, name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="edit-herd-breed">Breed</Label>
                <Input id="edit-herd-breed" data-testid="edit-herd-breed-input"
                  value={editingHerd.breed}
                  onChange={(e) => setEditingHerd({ ...editingHerd, breed: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label htmlFor="edit-herd-head">Head</Label>
                  <Input id="edit-herd-head" type="number" min="1" data-testid="edit-herd-head-input"
                    value={editingHerd.head_count}
                    onChange={(e) => setEditingHerd({ ...editingHerd, head_count: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="edit-herd-male">Male</Label>
                  <Input id="edit-herd-male" type="number" min="0"
                    value={editingHerd.male_count}
                    onChange={(e) => setEditingHerd({ ...editingHerd, male_count: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="edit-herd-female">Female</Label>
                  <Input id="edit-herd-female" type="number" min="0"
                    value={editingHerd.female_count}
                    onChange={(e) => setEditingHerd({ ...editingHerd, female_count: e.target.value })} />
                </div>
              </div>
              <div>
                <Label htmlFor="edit-herd-notes">Notes</Label>
                <Input id="edit-herd-notes" data-testid="edit-herd-notes-input"
                  value={editingHerd.notes}
                  onChange={(e) => setEditingHerd({ ...editingHerd, notes: e.target.value })} />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setEditingHerd(null)}>Cancel</Button>
                <Button className="flex-1" onClick={saveEditHerd} disabled={editHerdSaving}
                  data-testid="edit-herd-save-btn">
                  {editHerdSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {herdDetail?.herd?.photo_url ? (
                <img src={herdDetail.herd.photo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
              ) : (
                <span className="text-2xl">{animalIcons[herdDetail?.herd?.animal_type]}</span>
              )}
              {herdDetail?.herd?.name}
            </DialogTitle>
          </DialogHeader>
          {herdDetail && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Breed</p>
                  <p className="font-medium">{herdDetail.herd.breed}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Total Head</p>
                  <p className="font-medium">{herdDetail.herd.head_count}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Gender Breakdown</p>
                  <p className="font-medium">
                    {(herdDetail.herd.female_count > 0 || herdDetail.herd.male_count > 0) ? (
                      <>
                        <span className="text-pink-500">♀{herdDetail.herd.female_count || 0}</span>
                        {' / '}
                        <span className="text-blue-500">♂{herdDetail.herd.male_count || 0}</span>
                      </>
                    ) : 'Not specified'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Purpose</p>
                  <p className="font-medium">{purposeLabels[herdDetail.herd.purpose]}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <p className="font-medium capitalize">{herdDetail.herd.status}</p>
                </div>
              </div>
              
              {herdDetail.herd.notes && (
                <div>
                  <p className="text-muted-foreground text-sm">Notes</p>
                  <p className="text-sm">{herdDetail.herd.notes}</p>
                </div>
              )}

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-3">
                <Card className="bg-blue-50">
                  <CardContent className="py-3 text-center">
                    <Milk className="w-5 h-5 mx-auto text-blue-600" />
                    <p className="text-lg font-bold">{herdDetail.milk_logs?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Milk Logs</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50">
                  <CardContent className="py-3 text-center">
                    <Scale className="w-5 h-5 mx-auto text-green-600" />
                    <p className="text-lg font-bold">{herdDetail.weight_logs?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Weight Logs</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-50">
                  <CardContent className="py-3 text-center">
                    <Heart className="w-5 h-5 mx-auto text-red-600" />
                    <p className="text-lg font-bold">{herdDetail.health_records?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Health Records</p>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Health Records */}
              {herdDetail.health_records?.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm mb-2">Recent Health Records</h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {herdDetail.health_records.slice(0, 5).map((record) => (
                      <div key={record.id} className="text-sm p-2 bg-muted/50 rounded">
                        <span className="font-medium">{record.record_type}</span>: {record.description}
                        <p className="text-xs text-muted-foreground">{record.date}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Foundation Stock */}
              <div className="pt-3 border-t">
                <FoundationStock
                  animalType={herdDetail.herd.animal_type}
                  groupId={herdDetail.herd.id}
                  groupName={herdDetail.herd.name}
                  getAuthHeader={getAuthHeader}
                  breeds={getBreedsList(herdDetail.herd.animal_type).map(b => b.name)}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Actions Section - Enable when herds exist */}
      {herds[activeTab]?.length > 0 && (
        <Card className="card-farm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Quick Actions</CardTitle>
            <p className="text-sm text-muted-foreground">
              {selectedHerdForAction 
                ? `Selected: ${selectedHerdForAction.name}`
                : 'Click a herd below or in the list to select it'}
            </p>
          </CardHeader>
          <CardContent>
            {/* Herd selector mini-cards */}
            <div className="flex gap-2 overflow-x-auto pb-3 mb-3 border-b">
              {herds[activeTab]?.map((herd) => (
                <button
                  key={herd.id}
                  onClick={() => setSelectedHerdForAction(herd)}
                  className={`flex-shrink-0 p-2 rounded-lg border transition-all ${
                    selectedHerdForAction?.id === herd.id 
                      ? 'border-primary bg-primary/10' 
                      : 'border-muted hover:border-primary/50'
                  }`}
                >
                  <span className="text-lg">{animalIcons[activeTab]}</span>
                  <p className="text-xs font-medium truncate max-w-[80px]">{herd.name}</p>
                </button>
              ))}
            </div>
            
            {/* Action buttons */}
            {(() => {
              const noHerdSelected = !selectedHerdForAction;
              const isDairyHerd = selectedHerdForAction && (selectedHerdForAction.purpose === 'dairy' || selectedHerdForAction.purpose === 'dual');
              // Hide "Log Milk" entirely for non-dairy herds when one is selected.
              const showMilkButton = noHerdSelected || isDairyHerd;
              const milkTooltip = noHerdSelected ? 'Select a herd first' : null;
              const otherTooltip = noHerdSelected ? 'Select a herd first' : null;

              const ActionButton = ({ tooltip, disabled, onClick, icon: Icon, label, testId }) => {
                const btn = (
                  <Button
                    variant="outline"
                    className="flex-col h-auto py-4 w-full focus:ring-0 focus:ring-offset-0"
                    disabled={disabled}
                    onClick={onClick}
                    data-testid={testId}
                  >
                    <Icon className="w-5 h-5 mb-1" />
                    <span className="text-xs">{label}</span>
                  </Button>
                );
                if (!tooltip) return btn;
                return (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {/* span wrapper allows tooltip to fire on disabled button */}
                      <span tabIndex={0} className="inline-block w-full">{btn}</span>
                    </TooltipTrigger>
                    <TooltipContent>{tooltip}</TooltipContent>
                  </Tooltip>
                );
              };

              return (
                <TooltipProvider delayDuration={150}>
                  <div className={`grid ${showMilkButton ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3'} gap-3`}>
                    {showMilkButton && (
                      <ActionButton
                        tooltip={milkTooltip}
                        disabled={noHerdSelected}
                        onClick={() => setShowMilkDialog(true)}
                        icon={Milk}
                        label="Log Milk"
                        testId="quick-action-log-milk"
                      />
                    )}
                    <ActionButton
                      tooltip={otherTooltip}
                      disabled={noHerdSelected}
                      onClick={() => setShowWeightDialog(true)}
                      icon={Scale}
                      label="Log Weight"
                      testId="quick-action-log-weight"
                    />
                    <ActionButton
                      tooltip={otherTooltip}
                      disabled={noHerdSelected}
                      onClick={() => setShowHealthDialog(true)}
                      icon={Heart}
                      label="Health Record"
                      testId="quick-action-health-record"
                    />
                    <ActionButton
                      tooltip={otherTooltip}
                      disabled={noHerdSelected}
                      onClick={() => setShowPhotoDialog(true)}
                      icon={Camera}
                      label="Add Photo"
                      testId="quick-action-add-photo"
                    />
                  </div>
                </TooltipProvider>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Milk Production Dialog */}
      <Dialog open={showMilkDialog} onOpenChange={(open) => {
        setShowMilkDialog(open);
        if (!open) {
          setFoundationAnimals([]);
          setMilkLog({ quantity: '', unit: 'gallons', notes: '', date: todayLocal(), foundation_animal_id: '' });
        }
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Milk className="w-5 h-5 text-blue-500" />
              Log Milk Production
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Logging for: <span className="font-medium">{selectedHerdForAction?.name}</span>
            </p>
            <div className="flex gap-2">
              <Input
                type="number"
                step="0.1"
                placeholder="Quantity"
                value={milkLog.quantity}
                onChange={(e) => setMilkLog({ ...milkLog, quantity: e.target.value })}
                className="flex-1"
                data-testid="milk-quantity-input"
              />
              <Select value={milkLog.unit} onValueChange={(v) => setMilkLog({ ...milkLog, unit: v })}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gallons">Gallons</SelectItem>
                  <SelectItem value="liters">Liters</SelectItem>
                  <SelectItem value="lbs">Pounds</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Foundation Animal Assignment (Optional) */}
            {foundationAnimals.length > 0 && (
              <div>
                <label className="text-sm font-medium mb-1 block flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Assign to animal (optional)
                </label>
                <select
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  value={milkLog.foundation_animal_id}
                  onChange={(e) => setMilkLog({ ...milkLog, foundation_animal_id: e.target.value })}
                  data-testid="milk-animal-select"
                >
                  <option value="">Herd total only</option>
                  {foundationAnimals.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name} {a.tag_number ? `(#${a.tag_number})` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  If assigned, milk will also count toward herd total
                </p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium mb-1 block">Date</label>
              <Input
                type="date"
                value={milkLog.date}
                onChange={(e) => setMilkLog({ ...milkLog, date: e.target.value })}
              />
            </div>
            <Input
              placeholder="Notes (optional)"
              value={milkLog.notes}
              onChange={(e) => setMilkLog({ ...milkLog, notes: e.target.value })}
            />
            <Button onClick={handleLogMilk} className="w-full" data-testid="submit-milk-log-btn">
              Log Production
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Weight Log Dialog */}
      <Dialog open={showWeightDialog} onOpenChange={setShowWeightDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-green-500" />
              Log Weight
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Logging for: <span className="font-medium">{selectedHerdForAction?.name}</span>
            </p>
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="Weight"
                value={weightLog.weight}
                onChange={(e) => setWeightLog({ ...weightLog, weight: e.target.value })}
                className="flex-1"
              />
              <Select value={weightLog.unit} onValueChange={(v) => setWeightLog({ ...weightLog, unit: v })}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lbs">Pounds</SelectItem>
                  <SelectItem value="kg">Kilograms</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Input
              placeholder="Notes (optional)"
              value={weightLog.notes}
              onChange={(e) => setWeightLog({ ...weightLog, notes: e.target.value })}
            />
            <Button onClick={handleLogWeight} className="w-full">
              Log Weight
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Health Record Dialog */}
      <Dialog open={showHealthDialog} onOpenChange={setShowHealthDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              Add Health Record
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Recording for: <span className="font-medium">{selectedHerdForAction?.name}</span>
            </p>
            <Select value={healthRecord.record_type} onValueChange={(v) => setHealthRecord({ ...healthRecord, record_type: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Record Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vaccination">Vaccination</SelectItem>
                <SelectItem value="treatment">Treatment</SelectItem>
                <SelectItem value="checkup">Checkup</SelectItem>
                <SelectItem value="injury">Injury</SelectItem>
                <SelectItem value="deworming">Deworming</SelectItem>
                <SelectItem value="hoof_trim">Hoof Trim</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Description *"
              value={healthRecord.description}
              onChange={(e) => setHealthRecord({ ...healthRecord, description: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Vet Name (optional)"
                value={healthRecord.vet_name}
                onChange={(e) => setHealthRecord({ ...healthRecord, vet_name: e.target.value })}
              />
              <Input
                type="number"
                placeholder="Cost $ (optional)"
                value={healthRecord.cost}
                onChange={(e) => setHealthRecord({ ...healthRecord, cost: e.target.value })}
              />
            </div>
            <Input
              placeholder="Notes (optional)"
              value={healthRecord.notes}
              onChange={(e) => setHealthRecord({ ...healthRecord, notes: e.target.value })}
            />
            <Button onClick={handleAddHealth} className="w-full">
              Add Record
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Dialog */}
      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-purple-500" />
              Add Photo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Adding photo for: <span className="font-medium">{selectedHerdForAction?.name}</span>
            </p>
            
            {/* File Upload */}
            <div className="border-2 border-dashed border-muted rounded-lg p-4 text-center">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  
                  try {
                    // Show loading state for large files
                    const isLarge = file.size > 2 * 1024 * 1024;
                    if (isLarge) {
                      toast.info('Compressing image...', { duration: 2000 });
                    }
                    
                    // Compress image if needed (target: 2MB)
                    const compressedFile = await progressiveCompress(file, 2);
                    
                    const formData = new FormData();
                    formData.append('file', compressedFile);
                    formData.append('category', 'livestock');
                    
                    const res = await axios.post(`${API_URL}/api/upload/photo`, formData, {
                      headers: {
                        ...getAuthHeader(),
                        'Content-Type': 'multipart/form-data'
                      }
                    });
                    setPhotoUrl(res.data.url.startsWith('http') ? res.data.url : API_URL + res.data.url);
                    toast.success('Photo uploaded!');
                  } catch (error) {
                    console.error('Upload error:', error);
                    toast.error('Failed to upload photo');
                  }
                }}
                className="hidden"
                id="photo-upload"
              />
              <label htmlFor="photo-upload" className="cursor-pointer">
                <Camera className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  Click to upload a photo
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Any size - auto-compressed for you
                </p>
              </label>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="flex-1 h-px bg-muted"></div>
              <span className="text-xs text-muted-foreground">or paste URL</span>
              <div className="flex-1 h-px bg-muted"></div>
            </div>
            
            <Input
              placeholder="Photo URL (https://...)"
              value={photoUrl}
              onChange={(e) => setPhotoUrl(e.target.value)}
            />
            
            {photoUrl && (
              <div className="relative">
                <img 
                  src={photoUrl} 
                  alt="Preview" 
                  className="w-full h-40 object-cover rounded-lg"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
            <Button onClick={handleUpdatePhoto} className="w-full" disabled={!photoUrl}>
              Save Photo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Birth Stats Full Report Dialog */}
      <Dialog open={showBirthStatsDialog} onOpenChange={setShowBirthStatsDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Baby className="w-5 h-5 text-pink-500" />
              Season Birth Report
            </DialogTitle>
          </DialogHeader>
          
          {birthStats && (
            <div className="space-y-4 mt-2">
              {/* Period */}
              <p className="text-sm text-muted-foreground text-center">
                {birthStats.period?.start && new Date(birthStats.period.start).toLocaleDateString()} 
                {' - '}
                {birthStats.period?.end && new Date(birthStats.period.end).toLocaleDateString()}
              </p>

              {/* Main Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 rounded-lg bg-pink-50 text-center">
                  <p className="text-3xl font-bold text-pink-700">{birthStats.total_births}</p>
                  <p className="text-sm text-pink-600">Total Births</p>
                </div>
                <div className="p-4 rounded-lg bg-blue-50 text-center">
                  <p className="text-3xl font-bold text-blue-700">{birthStats.total_babies}</p>
                  <p className="text-sm text-blue-600">Total Babies</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-green-50 text-center">
                  <p className="text-xl font-bold text-green-700">{birthStats.livability_rate || 0}%</p>
                  <p className="text-xs text-green-600">Success Rate</p>
                </div>
                <div className="p-3 rounded-lg bg-purple-50 text-center">
                  <p className="text-xl font-bold text-purple-700">{birthStats.avg_babies_per_birth || 0}</p>
                  <p className="text-xs text-purple-600">Avg per Birth</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-50 text-center">
                  <p className="text-xl font-bold text-amber-700">{birthStats.profiles_created || 0}</p>
                  <p className="text-xs text-amber-600">Profiles Made</p>
                </div>
              </div>

              {/* Outcomes Breakdown */}
              <div className="p-4 rounded-lg bg-muted/30">
                <h4 className="font-medium text-sm mb-3">Outcomes</h4>
                <div className="space-y-2">
                  {birthStats.outcomes?.thriving_with_mom > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-green-700">Thriving with mom</span>
                      <span className="font-medium">{birthStats.outcomes.thriving_with_mom}</span>
                    </div>
                  )}
                  {birthStats.outcomes?.needed_help_nursing > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-yellow-700">Needed help nursing</span>
                      <span className="font-medium">{birthStats.outcomes.needed_help_nursing}</span>
                    </div>
                  )}
                  {birthStats.outcomes?.bottle_baby > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-orange-700">Bottle babies</span>
                      <span className="font-medium">{birthStats.outcomes.bottle_baby}</span>
                    </div>
                  )}
                  {birthStats.outcomes?.fostered > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-blue-700">Fostered</span>
                      <span className="font-medium">{birthStats.outcomes.fostered}</span>
                    </div>
                  )}
                  {birthStats.outcomes?.stillborn > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Stillborn</span>
                      <span className="font-medium">{birthStats.outcomes.stillborn}</span>
                    </div>
                  )}
                  {birthStats.outcomes?.did_not_make_it > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Lost after birth</span>
                      <span className="font-medium">{birthStats.outcomes.did_not_make_it}</span>
                    </div>
                  )}
                  {birthStats.outcomes?.unknown > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Unknown/Other</span>
                      <span className="font-medium">{birthStats.outcomes.unknown}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* By Sex */}
              {birthStats.by_sex && (
                <div className="p-4 rounded-lg bg-muted/30">
                  <h4 className="font-medium text-sm mb-3">By Sex</h4>
                  <div className="flex gap-4 justify-center">
                    <div className="text-center">
                      <span className="text-2xl font-bold text-pink-500">♀ {birthStats.by_sex.female || 0}</span>
                      <p className="text-xs text-muted-foreground">Females</p>
                    </div>
                    <div className="text-center">
                      <span className="text-2xl font-bold text-blue-500">♂ {birthStats.by_sex.male || 0}</span>
                      <p className="text-xs text-muted-foreground">Males</p>
                    </div>
                    <div className="text-center">
                      <span className="text-2xl font-bold text-gray-400">? {birthStats.by_sex.unknown || 0}</span>
                      <p className="text-xs text-muted-foreground">Unknown</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Top Dams */}
              {birthStats.by_dam && Object.keys(birthStats.by_dam).length > 0 && (
                <div className="p-4 rounded-lg bg-muted/30">
                  <h4 className="font-medium text-sm mb-3">By Dam</h4>
                  <div className="space-y-2 max-h-[150px] overflow-y-auto">
                    {Object.entries(birthStats.by_dam)
                      .sort((a, b) => b[1].babies - a[1].babies)
                      .slice(0, 10)
                      .map(([damId, data]) => (
                        <div key={damId} className="flex items-center justify-between text-sm">
                          <span>{data.name}</span>
                          <span className="text-muted-foreground">
                            {data.births} birth{data.births !== 1 ? 's' : ''}, {data.babies} {data.babies === 1 ? 'baby' : 'babies'}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowBirthStatsDialog(false)}
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LivestockManager;
